import React, { useRef, useState, useEffect, useCallback } from 'react';

if (typeof window !== 'undefined') {
  const _err = window.onerror;
  window.onerror = (msg, ...args) => {
    if (typeof msg === 'string' && msg.includes('ResizeObserver loop')) return true;
    return _err?.(...args);
  };
}

const HANDLE_RADIUS = 8;

/**
 * AdminZoneDrawer
 *
 * Draws BOTH photo zones (amber) and text zones (violet) on the same canvas.
 *
 * Props:
 *  previewImageUrl  – URL of the preview/thumbnail image
 *  initialZones     – { photo: Zone[], text: Zone[] }
 *  maxPhotoZones    – number of photo zones to draw (from imageCount)
 *  maxTextZones     – number of text zones to draw  (from textCount)
 *  onChange         – ({ photo, text }) => void
 */
export default function AdminZoneDrawer({
  previewImageUrl,
  initialZones = { photo: [], text: [] },
  maxPhotoZones = 0,
  maxTextZones  = 0,
  onChange,
}) {
  const canvasRef       = useRef(null);
  const containerRef    = useRef(null);
  const imgRef          = useRef(new Image());
  const naturalRatioRef = useRef(1);

  // Separate refs for photo / text zones
  const photoZonesRef = useRef([]);
  const textZonesRef  = useRef([]);

  const drawingRef    = useRef(false);
  const startPtRef    = useRef(null);
  const liveRectRef   = useRef(null);
  const dragRef       = useRef(null);   // { kind, zoneIdx, corner }

  const seedRef   = useRef({ photo: null, text: null });
  const seededRef = useRef({ photo: false, text: false });

  const [photoZones,    setPhotoZones]    = useState([]);
  const [textZones,     setTextZones]     = useState([]);
  const [activeKind,    setActiveKind]    = useState('photo'); // 'photo' | 'text'
  const [imgReady,      setImgReady]      = useState(false);
  const [hoveredHandle, setHoveredHandle] = useState(null);
  const [,              forceUpdate]      = useState(0);

  /* ── zone geometry helpers ── */
  const rectToQuad = (z) => ({
    TL: { x: z.x,           y: z.y            },
    TR: { x: z.x + z.width, y: z.y            },
    BR: { x: z.x + z.width, y: z.y + z.height },
    BL: { x: z.x,           y: z.y + z.height },
  });

  const quadToRect = (q) => {
    const xs = [q.TL.x, q.TR.x, q.BR.x, q.BL.x];
    const ys = [q.TL.y, q.TR.y, q.BR.y, q.BL.y];
    const x  = Math.min(...xs);
    const y  = Math.min(...ys);
    return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y, quad: q };
  };

  /* keep refs in sync with state */
  useEffect(() => { photoZonesRef.current = photoZones; }, [photoZones]);
  useEffect(() => { textZonesRef.current  = textZones;  }, [textZones]);

  /* ── Seed zones ONCE when initialZones first arrive ── */
  useEffect(() => {
    ['photo', 'text'].forEach((kind) => {
      if (seededRef.current[kind]) return;
      const arr = initialZones[kind];
      if (!arr || arr.length === 0) return;
      const converted = arr.map(z => z.quad ? z : { ...z, quad: rectToQuad(z) });
      seedRef.current[kind]   = converted;
      seededRef.current[kind] = true;
      if (kind === 'photo') { photoZonesRef.current = converted; setPhotoZones(converted); }
      else                  { textZonesRef.current  = converted; setTextZones(converted);  }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(initialZones)]);

  /* Reset seed when image changes */
  useEffect(() => {
    seededRef.current = { photo: false, text: false };
    seedRef.current   = { photo: null,  text: null  };
  }, [previewImageUrl]);

  /* ── canvas coordinate helpers ── */
  const pctToPx = useCallback((pct, axis) => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    return (pct / 100) * (axis === 'x' ? canvas.width : canvas.height);
  }, []);

  const pxToPct = useCallback((px, axis) => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    return +((px / (axis === 'x' ? canvas.width : canvas.height)) * 100).toFixed(2);
  }, []);

  const quadPx = useCallback((q) => ({
    TL: { x: pctToPx(q.TL.x, 'x'), y: pctToPx(q.TL.y, 'y') },
    TR: { x: pctToPx(q.TR.x, 'x'), y: pctToPx(q.TR.y, 'y') },
    BR: { x: pctToPx(q.BR.x, 'x'), y: pctToPx(q.BR.y, 'y') },
    BL: { x: pctToPx(q.BL.x, 'x'), y: pctToPx(q.BL.y, 'y') },
  }), [pctToPx]);

  /* ── paint ── */
  const paint = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !imgReady) return;
    const ctx = canvas.getContext('2d');
    const cw  = canvas.width;
    const ch  = canvas.height;
    const img = imgRef.current;

    ctx.clearRect(0, 0, cw, ch);
    ctx.drawImage(img, 0, 0, cw, ch);
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    ctx.fillRect(0, 0, cw, ch);

    const drawZones = (zones, kind) => {
      const isPhoto   = kind === 'photo';
      const mainColor = isPhoto ? '#F59E0B' : '#8B5CF6';
      const bgColor   = isPhoto ? 'rgba(245,158,11,0.92)' : 'rgba(139,92,246,0.92)';
      const lightBg   = isPhoto ? 'rgba(245,158,11,0.10)' : 'rgba(139,92,246,0.10)';

      zones.forEach((z, i) => {
        const q = z.quad ? quadPx(z.quad) : quadPx(rectToQuad(z));

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(q.TL.x, q.TL.y);
        ctx.lineTo(q.TR.x, q.TR.y);
        ctx.lineTo(q.BR.x, q.BR.y);
        ctx.lineTo(q.BL.x, q.BL.y);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(img, 0, 0, cw, ch);
        ctx.fillStyle = lightBg;
        ctx.fillRect(0, 0, cw, ch);
        ctx.restore();

        ctx.beginPath();
        ctx.moveTo(q.TL.x, q.TL.y);
        ctx.lineTo(q.TR.x, q.TR.y);
        ctx.lineTo(q.BR.x, q.BR.y);
        ctx.lineTo(q.BL.x, q.BL.y);
        ctx.closePath();
        ctx.strokeStyle = mainColor;
        ctx.lineWidth   = 2;
        ctx.setLineDash([]);
        ctx.stroke();

        // ── label: small badge at top-left + centered label inside the zone ──
        const labelText = isPhoto ? `📸 Photo ${i + 1}` : `✏️ Text ${i + 1}`;

        // Top-left badge
        ctx.fillStyle = bgColor;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(q.TL.x + 6, q.TL.y + 6, 100, 20, 5);
        else ctx.rect(q.TL.x + 6, q.TL.y + 6, 100, 20);
        ctx.fill();
        ctx.fillStyle    = '#fff';
        ctx.font         = 'bold 10px sans-serif';
        ctx.textAlign    = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(labelText, q.TL.x + 10, q.TL.y + 16);

        // Centered label inside the zone (geometric center of the quad)
        const centerX = (q.TL.x + q.TR.x + q.BR.x + q.BL.x) / 4;
        const centerY = (q.TL.y + q.TR.y + q.BR.y + q.BL.y) / 4;
        const centerLabel = isPhoto ? `Photo Zone ${i + 1}` : `Text Zone ${i + 1}`;
        ctx.font         = 'bold 13px sans-serif';
        ctx.textAlign    = 'center';
        ctx.textBaseline = 'middle';
        // shadow for readability
        ctx.shadowColor   = 'rgba(0,0,0,0.7)';
        ctx.shadowBlur    = 4;
        ctx.fillStyle     = '#ffffff';
        ctx.fillText(centerLabel, centerX, centerY);
        ctx.shadowBlur    = 0;
        ctx.shadowColor   = 'transparent';
        ctx.textAlign    = 'left';
        ctx.textBaseline = 'alphabetic';

        // corner handles
        Object.entries(q).forEach(([corner, pt]) => {
          const isHovered  = hoveredHandle?.kind === kind && hoveredHandle?.zoneIdx === i && hoveredHandle?.corner === corner;
          const isDragging = dragRef.current?.kind === kind && dragRef.current?.zoneIdx === i && dragRef.current?.corner === corner;
          ctx.beginPath();
          ctx.arc(pt.x, pt.y, HANDLE_RADIUS, 0, Math.PI * 2);
          ctx.fillStyle   = isDragging ? '#ef4444' : isHovered ? '#3b82f6' : '#ffffff';
          ctx.strokeStyle = isDragging ? '#b91c1c' : mainColor;
          ctx.lineWidth   = 2;
          ctx.fill();
          ctx.stroke();
          ctx.fillStyle    = isDragging || isHovered ? '#fff' : '#374151';
          ctx.font         = 'bold 9px sans-serif';
          ctx.textAlign    = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(corner, pt.x, pt.y);
          ctx.textAlign    = 'left';
          ctx.textBaseline = 'alphabetic';
        });
      });
    };

    drawZones(photoZonesRef.current, 'photo');
    drawZones(textZonesRef.current,  'text');

    // live drawing rect
    const lr = liveRectRef.current;
    if (lr) {
      const col = activeKind === 'photo' ? '#F59E0B' : '#8B5CF6';
      ctx.strokeStyle = col;
      ctx.lineWidth   = 1.5;
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(lr.x, lr.y, lr.w, lr.h);
      ctx.fillStyle = activeKind === 'photo' ? 'rgba(245,158,11,0.10)' : 'rgba(139,92,246,0.10)';
      ctx.fillRect(lr.x, lr.y, lr.w, lr.h);
      ctx.setLineDash([]);
    }
  }, [imgReady, hoveredHandle, quadPx, activeKind]);

  /* ── size canvas ── */
  const sizeCanvas = useCallback(() => {
    const canvas    = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const w = container.clientWidth;
    if (w <= 0) return;
    const h = Math.min(Math.round(w * naturalRatioRef.current), 520);
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width  = w;
      canvas.height = h;
    }
    paint();
  }, [paint]);

  /* ── load image ── */
  const prevUrlRef = useRef('');
  useEffect(() => {
    if (!previewImageUrl) { setImgReady(false); return; }
    if (prevUrlRef.current !== previewImageUrl) {
      prevUrlRef.current = previewImageUrl;
      setImgReady(false);
    }
    const img = imgRef.current;
    img.crossOrigin = 'anonymous';
    const onLoad  = () => { naturalRatioRef.current = img.naturalHeight / img.naturalWidth; setImgReady(true); };
    const onError = () => console.error('AdminZoneDrawer: failed to load', previewImageUrl);
    img.addEventListener('load',  onLoad);
    img.addEventListener('error', onError);
    img.src = previewImageUrl;
    return () => { img.removeEventListener('load', onLoad); img.removeEventListener('error', onError); };
  }, [previewImageUrl]);

  useEffect(() => {
    if (!imgReady) return;
    sizeCanvas();
    let rafId;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => sizeCanvas());
    });
    if (containerRef.current) ro.observe(containerRef.current);
    return () => { ro.disconnect(); cancelAnimationFrame(rafId); };
  }, [imgReady, sizeCanvas]);

  useEffect(() => { paint(); }, [paint, photoZones, textZones, activeKind]);

  /* ── findHandle: searches both zone sets ── */
  const findHandle = useCallback((pos) => {
    const searchIn = (zones, kind) => {
      for (let i = zones.length - 1; i >= 0; i--) {
        const z = zones[i];
        const q = z.quad ? quadPx(z.quad) : quadPx(rectToQuad(z));
        for (const [corner, pt] of Object.entries(q)) {
          if (Math.hypot(pos.x - pt.x, pos.y - pt.y) <= HANDLE_RADIUS + 4)
            return { kind, zoneIdx: i, corner };
        }
      }
      return null;
    };
    return searchIn(photoZonesRef.current, 'photo') || searchIn(textZonesRef.current, 'text');
  }, [quadPx]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect   = canvas.getBoundingClientRect();
    const src    = e.touches ? e.touches[0] : e;
    return {
      x: ((src.clientX - rect.left) / rect.width)  * canvas.width,
      y: ((src.clientY - rect.top)  / rect.height) * canvas.height,
    };
  };

  const notifyChange = (pZones, tZones) => {
    onChange?.({ photo: pZones, text: tZones });
  };

  /* ── pointer handlers ── */
  const onDown = (e) => {
    e.preventDefault();
    const pos    = getPos(e);
    const handle = findHandle(pos);
    if (handle) { dragRef.current = handle; return; }

    const currentZones = activeKind === 'photo' ? photoZonesRef.current : textZonesRef.current;
    const maxZones     = activeKind === 'photo' ? maxPhotoZones : maxTextZones;
    if (currentZones.length >= maxZones) return;

    drawingRef.current  = true;
    startPtRef.current  = pos;
    liveRectRef.current = { x: pos.x, y: pos.y, w: 0, h: 0 };
    paint();
  };

  const onMove = (e) => {
    e.preventDefault();
    const pos = getPos(e);

    if (dragRef.current) {
      const { kind, zoneIdx, corner } = dragRef.current;
      const zonesRef = kind === 'photo' ? photoZonesRef : textZonesRef;
      const setZones = kind === 'photo' ? setPhotoZones : setTextZones;

      const updated = zonesRef.current.map((z, i) => {
        if (i !== zoneIdx) return z;
        const q = z.quad ? { ...z.quad } : rectToQuad(z);
        q[corner] = { x: pxToPct(pos.x, 'x'), y: pxToPct(pos.y, 'y') };
        return { ...quadToRect(q), quad: q };
      });
      zonesRef.current = updated;
      setZones(updated);
      notifyChange(
        kind === 'photo' ? updated : photoZonesRef.current,
        kind === 'text'  ? updated : textZonesRef.current,
      );
      paint();
      return;
    }

    const handle = findHandle(pos);
    setHoveredHandle(handle);

    const currentZones = activeKind === 'photo' ? photoZonesRef.current : textZonesRef.current;
    const maxZones     = activeKind === 'photo' ? maxPhotoZones : maxTextZones;
    if (canvasRef.current)
      canvasRef.current.style.cursor = handle ? 'grab'
        : currentZones.length >= maxZones ? 'not-allowed' : 'crosshair';

    if (!drawingRef.current || !startPtRef.current) return;
    const sp = startPtRef.current;
    liveRectRef.current = {
      x: Math.min(sp.x, pos.x), y: Math.min(sp.y, pos.y),
      w: Math.abs(pos.x - sp.x), h: Math.abs(pos.y - sp.y),
    };
    paint();
  };

  const onUp = (e) => {
    e?.preventDefault();
    if (dragRef.current) { dragRef.current = null; forceUpdate(n => n + 1); return; }
    if (!drawingRef.current) return;
    drawingRef.current = false;
    const lr = liveRectRef.current;
    if (!lr || lr.w < 12 || lr.h < 12) { liveRectRef.current = null; paint(); return; }

    const rect   = {
      x:      pxToPct(lr.x, 'x'),
      y:      pxToPct(lr.y, 'y'),
      width:  pxToPct(lr.w, 'x'),
      height: pxToPct(lr.h, 'y'),
    };
    const quad    = rectToQuad(rect);
    const newZone = { ...rect, quad };
    liveRectRef.current = null;

    if (activeKind === 'photo') {
      const updated = [...photoZonesRef.current, newZone];
      photoZonesRef.current = updated;
      setPhotoZones(updated);
      notifyChange(updated, textZonesRef.current);
    } else {
      const updated = [...textZonesRef.current, newZone];
      textZonesRef.current = updated;
      setTextZones(updated);
      notifyChange(photoZonesRef.current, updated);
    }
    forceUpdate(n => n + 1);
  };

  const removeZone = (kind, idx) => {
    if (kind === 'photo') {
      const updated = photoZonesRef.current.filter((_, i) => i !== idx);
      photoZonesRef.current = updated;
      setPhotoZones(updated);
      notifyChange(updated, textZonesRef.current);
    } else {
      const updated = textZonesRef.current.filter((_, i) => i !== idx);
      textZonesRef.current = updated;
      setTextZones(updated);
      notifyChange(photoZonesRef.current, updated);
    }
  };

  const clearAll = () => {
    photoZonesRef.current = [];
    textZonesRef.current  = [];
    seededRef.current     = { photo: false, text: false };
    setPhotoZones([]);
    setTextZones([]);
    notifyChange([], []);
  };

  const photoComplete = photoZones.length >= maxPhotoZones && maxPhotoZones > 0;
  const textComplete  = textZones.length  >= maxTextZones  && maxTextZones  > 0;
  const allComplete   = photoComplete && textComplete;

  /* ── render ── */
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>

      {/* ── Mode switcher ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>Drawing mode:</span>

        <button
          type="button"
          onClick={() => setActiveKind('photo')}
          style={{
            padding: '6px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
            border: activeKind === 'photo' ? '2px solid #F59E0B' : '2px solid #d1d5db',
            background: activeKind === 'photo' ? '#FEF3C7' : '#f9fafb',
            color: activeKind === 'photo' ? '#92400e' : '#6b7280',
            cursor: 'pointer', transition: 'all 0.15s',
          }}>
          📸 Photo Zones
          <span style={{
            marginLeft: '6px', padding: '1px 7px', borderRadius: '12px', fontSize: '11px',
            background: photoComplete ? '#10b981' : '#F59E0B', color: '#fff',
          }}>
            {photoZones.length}/{maxPhotoZones}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveKind('text')}
          style={{
            padding: '6px 16px', borderRadius: '20px', fontSize: '12px', fontWeight: 600,
            border: activeKind === 'text' ? '2px solid #8B5CF6' : '2px solid #d1d5db',
            background: activeKind === 'text' ? '#EDE9FE' : '#f9fafb',
            color: activeKind === 'text' ? '#4c1d95' : '#6b7280',
            cursor: 'pointer', transition: 'all 0.15s',
          }}>
          ✏️ Text Zones
          <span style={{
            marginLeft: '6px', padding: '1px 7px', borderRadius: '12px', fontSize: '11px',
            background: textComplete ? '#10b981' : '#8B5CF6', color: '#fff',
          }}>
            {textZones.length}/{maxTextZones}
          </span>
        </button>

        {(photoZones.length > 0 || textZones.length > 0) && (
          <button
            type="button"
            onClick={clearAll}
            style={{
              marginLeft: 'auto', fontSize: '12px', padding: '5px 12px',
              borderRadius: '6px', border: '1px solid #dc3545', color: '#dc3545',
              background: 'transparent', cursor: 'pointer',
            }}>
            Clear all
          </button>
        )}
      </div>

      {/* ── Active mode hint ── */}
      <div style={{
        padding: '8px 12px', borderRadius: '8px', fontSize: '12px',
        background: activeKind === 'photo' ? '#FFFBEB' : '#F5F3FF',
        border: `1px solid ${activeKind === 'photo' ? '#FCD34D' : '#C4B5FD'}`,
        color: activeKind === 'photo' ? '#92400e' : '#4c1d95',
      }}>
        {activeKind === 'photo'
          ? `📸 Click & drag to draw Photo Zones (${maxPhotoZones - photoZones.length} remaining). Drag white corner handles to adjust perspective.`
          : `✏️ Click & drag to draw Text Zones (${maxTextZones - textZones.length} remaining). Drag white corner handles to adjust perspective.`
        }
      </div>

      {/* ── Canvas ── */}
      <div
        ref={containerRef}
        style={{
          width: '100%', maxWidth: '600px', borderRadius: '10px', overflow: 'hidden',
          border: `2px dashed ${activeKind === 'photo' ? '#F59E0B' : '#8B5CF6'}`,
          background: '#f0f4ff', position: 'relative', minHeight: '160px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'border-color 0.2s',
        }}>
        {!imgReady && (
          <div style={{ position: 'absolute', fontSize: '13px', color: '#adb5bd' }}>
            {previewImageUrl ? '⏳ Loading…' : 'No preview image'}
          </div>
        )}
        <canvas
          ref={canvasRef}
          style={{ display: 'block', width: '100%', touchAction: 'none', opacity: imgReady ? 1 : 0 }}
          onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
          onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}
        />
      </div>

      {/* ── Completion status ── */}
      {imgReady && !allComplete && (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {!photoComplete && maxPhotoZones > 0 && (
            <div style={{ padding: '7px 12px', borderRadius: '8px', background: '#fff3cd', color: '#856404', fontSize: '13px' }}>
              ⚠️ {maxPhotoZones - photoZones.length} more Photo Zone(s) needed
            </div>
          )}
          {!textComplete && maxTextZones > 0 && (
            <div style={{ padding: '7px 12px', borderRadius: '8px', background: '#ede9fe', color: '#4c1d95', fontSize: '13px' }}>
              ⚠️ {maxTextZones - textZones.length} more Text Zone(s) needed
            </div>
          )}
        </div>
      )}

      {imgReady && allComplete && (
        <div style={{ padding: '8px 12px', borderRadius: '8px', background: '#d1fae5', color: '#065f46', fontSize: '13px', fontWeight: 600 }}>
          ✅ All zones drawn! Photo: {photoZones.length}/{maxPhotoZones} · Text: {textZones.length}/{maxTextZones}
        </div>
      )}

      {/* ── Zone cards ── */}
      {(photoZones.length > 0 || textZones.length > 0) && (
        <div>
          <p style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>Drawn Zones</p>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {photoZones.map((z, i) => (
              <ZoneCard key={`photo-${i}`} zone={z} index={i} kind="photo" onRemove={() => removeZone('photo', i)} />
            ))}
            {textZones.map((z, i) => (
              <ZoneCard key={`text-${i}`} zone={z} index={i} kind="text" onRemove={() => removeZone('text', i)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ZoneCard({ zone, index, kind, onRemove }) {
  const isPhoto   = kind === 'photo';
  const mainColor = isPhoto ? '#F59E0B' : '#8B5CF6';
  const bgColor   = isPhoto ? '#fffbeb' : '#f5f3ff';
  const label     = isPhoto ? `📸 Photo Zone ${index + 1}` : `✏️ Text Zone ${index + 1}`;

  return (
    <div style={{
      background: bgColor, border: `1.5px solid ${mainColor}`,
      borderRadius: '8px', padding: '10px 12px', fontSize: '12px', minWidth: '160px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
        <span style={{ fontWeight: 700, color: mainColor }}>{label}</span>
        <button
          type="button"
          onClick={onRemove}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545', fontSize: '14px', lineHeight: 1, padding: 0 }}>
          ✕
        </button>
      </div>
      {zone.quad ? (
        <div style={{ color: '#6b7280', lineHeight: 1.8, fontSize: '11px' }}>
          <div>TL: ({zone.quad.TL.x}%, {zone.quad.TL.y}%)</div>
          <div>TR: ({zone.quad.TR.x}%, {zone.quad.TR.y}%)</div>
          <div>BR: ({zone.quad.BR.x}%, {zone.quad.BR.y}%)</div>
          <div>BL: ({zone.quad.BL.x}%, {zone.quad.BL.y}%)</div>
        </div>
      ) : (
        <div style={{ color: '#6b7280', lineHeight: 1.7, fontSize: '11px' }}>
          <div>X: {zone.x}% Y: {zone.y}%</div>
          <div>W: {zone.width}% H: {zone.height}%</div>
        </div>
      )}
    </div>
  );
}